/**
 * The one request core every client in the web app uses: `lib/api.ts`, the typed calls below,
 * the P&ID calls and the register's downloads.
 *
 * Its rules, in one place: the session cookie rides along; a 401 hands the browser to Microsoft
 * and back to the same address and query; any other failure is an `ApiError` carrying the status
 * and the server's reason in words. Every path segment built from a value goes through `path` or
 * `enc`, so an id from a crafted link stays one segment (SEC-20).
 */
import type { paths } from "@neio/contracts";

export const BASE: string = import.meta.env.VITE_API_BASE ?? "/api/v1";

/** One path segment, encoded. `/` and `..` in a value stay inside the segment. */
export const enc = (value: string | number): string => encodeURIComponent(String(value));

/** A path whose every interpolated value is one encoded segment: path`/uc1/jobs/${id}/result`. */
export function path(strings: TemplateStringsArray, ...values: (string | number)[]): string {
  return strings.reduce((out, part, i) => out + part + (i < values.length ? enc(values[i]) : ""), "");
}

/** A refusal from the API, with its status and the server's reason, which is meant to be read. */
export class ApiError extends Error {
  constructor(public readonly status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
  }
}

export interface RequestOptions extends RequestInit {
  /** Send the browser to sign in on 401. On by default; `fetchMe` and sign-out turn it off. */
  signIn?: boolean;
}

/** Where sign-in starts, coming back to this address and its query. */
export function signInUrl(): string {
  return `${BASE}/auth/login?next=${enc(window.location.pathname + window.location.search)}`;
}

function toSignIn(): Promise<never> {
  // A full navigation, not fetch: the identity provider will not answer inside XHR and must not
  // be framed.
  window.location.href = signInUrl();
  // Never settles. The navigation replaces the page.
  return new Promise<never>(() => undefined);
}

function detailOf(body: unknown, res: Response): string {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  // A structured refusal, for example the queue-full 429: {message, queue_depth, limit}.
  const message = (detail as { message?: unknown } | null)?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(detail)) {
    // FastAPI's 422: a list of {loc, msg}. The first message is the useful one.
    const first = detail[0] as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }
  return `${res.status} ${res.statusText}`.trim();
}

/**
 * Fetch a URL on this site under the core's rules and return the response, which is ok. For a
 * body that is not JSON: a multipart upload, or a file download.
 */
export async function fetchOk(href: string, init: RequestOptions = {}): Promise<Response> {
  const { signIn = true, headers, ...rest } = init;
  // A JSON body says so. A FormData body must not: the browser writes the multipart boundary.
  const json = rest.body === undefined || typeof rest.body === "string";
  const res = await fetch(href, {
    ...rest,
    // The session is an httpOnly cookie, never touched by this code. `same-origin` is what makes
    // the browser attach it.
    credentials: "same-origin",
    headers: json ? { "Content-Type": "application/json", ...(headers ?? {}) } : headers,
  });
  // Only 401 means "sign in". A 503 is a refusal with a reason (an audit write failed, or sign-in
  // is not configured here), so it is thrown like any other refusal and the screen shows it.
  if (signIn && res.status === 401) return toSignIn();
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, detailOf(body, res));
  }
  return res;
}

/** A JSON call by path under the API base, for example `request("/cases")`. */
export async function request<T>(path: string, init: RequestOptions = {}): Promise<T> {
  const res = await fetchOk(`${BASE}${path}`, init);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

type Json<R> = R extends { content: { "application/json": infer B } } ? B : unknown;
type Ok<Op> = Op extends { responses: infer Rs }
  ? 200 extends keyof Rs
    ? Json<Rs[200]>
    : 201 extends keyof Rs
      ? Json<Rs[201]>
      : 202 extends keyof Rs
        ? Json<Rs[202]>
        : unknown
  : unknown;
type Params<Op, K extends "path" | "query"> = Op extends { parameters: { [P in K]?: infer X } }
  ? Exclude<X, undefined>
  : never;
type Body<Op> = Op extends { requestBody?: { content: { "application/json": infer B } } } ? B : never;

type PathsWith<M extends string> = {
  [P in keyof paths]: paths[P] extends { [K in M]: infer Op } ? (Op extends undefined | never ? never : P) : never;
}[keyof paths];

export interface CallParams<Op> {
  path?: Params<Op, "path">;
  query?: Params<Op, "query">;
}

type UrlParams = { path?: object; query?: object };

function url(template: string, params: UrlParams = {}): string {
  let filled = template.replace(/^\/api\/v1/, "");
  for (const [key, value] of Object.entries(params.path ?? {})) {
    filled = filled.replace(`{${key}}`, enc(String(value)));
  }
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params.query ?? {})) {
    if (value !== undefined && value !== null) query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `${filled}?${qs}` : filled;
}

/** GET a contract path, typed by its 200 response. */
export function apiGet<P extends PathsWith<"get">>(
  path: P,
  params?: CallParams<paths[P]["get"]>,
): Promise<Ok<paths[P]["get"]>> {
  return request(url(path, params as UrlParams | undefined));
}

/** POST to a contract path, typed by its body and its success response. */
export function apiSend<P extends PathsWith<"post">>(
  path: P,
  body: Body<paths[P]["post"]> | undefined,
  params?: CallParams<paths[P]["post"]>,
): Promise<Ok<paths[P]["post"]>> {
  return request(url(path, params as UrlParams | undefined), {
    method: "POST",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** A status payload that says whether a feature runs here, for example `GET /pid/status`. */
interface FeatureStatus { enabled: boolean; reason?: string | null }
/** A refusal already read, for example a module start's `Refusal`. */
interface ReadRefusal { kind: string; detail: string }

/** The server's words for a switch that is off. Recognized on any route, whatever the status. */
const NOT_ENABLED = /\bnot enabled\b/i;

/**
 * Why a feature is switched off here, or null when what was given says nothing of the kind. The
 * one place the screens decide "off" from:
 *
 * - a status payload with `enabled: false` (P&ID's `/pid/status`): its reason, or a plain one;
 * - a refusal already read with `kind: "off"` (a module start): its detail;
 * - an `ApiError` whose reason says the feature is "not enabled" (cases, P&ID), on any route;
 * - an `ApiError` with one of `statuses`, for a route that exists only while its switch is on.
 *   Pass `[]` on a route where a 404 can also mean "no such item", for example `GET /cases/{id}`.
 */
export function switchedOff(signal: unknown, statuses: number[] = [404, 409]): string | null {
  if (signal instanceof ApiError) {
    return NOT_ENABLED.test(signal.message) || statuses.includes(signal.status) ? signal.message : null;
  }
  if (signal && typeof signal === "object") {
    const status = signal as Partial<FeatureStatus>;
    if (status.enabled === false) return status.reason || "This is not enabled on this deployment.";
    const read = signal as Partial<ReadRefusal>;
    if (read.kind === "off") return read.detail ?? "";
  }
  return null;
}
